"use client"
import { useState } from "react"
import { FaFileUpload, FaFileAlt, FaEye, FaTrash, FaTrashAlt, FaTimes } from "react-icons/fa"
import { getDatabase, ref, push, set, remove } from "firebase/database"
import { getAuth } from "firebase/auth"

const UploadsSection = ({
  selectedUser,
  mtcFiles = [],
  statementFiles = [],
  formatsFiles = [],
  uploadProgress = {},
  handleFileUpload,
  onDeleteFile,     // expected: async (categoryKey, fileId) => ...
  onDeleteAll,      // optional: async (categoryKey) => ...
}) => {
  const [activeSection, setActiveSection] = useState("mtc")
  const [selectMode, setSelectMode] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState(new Set())

  const sections = [
    {
      key: "mtc",
      label: "MTC Documents",
      title: "Material Test Certificate (MTC)",
      description: "Upload MTC files for this user. These will be available to all users.",
      files: mtcFiles,
      inputId: "mtc-upload",
    },
    {
      key: "statement",
      label: "User Statements",
      title: "User Statements",
      description: "Upload statement files specific to this user.",
      files: statementFiles,
      inputId: "statement-upload",
    },
    {
      key: "formats",
      label: "Document Formats",
      title: "Document Formats",
      description: "Upload document format templates for this user.",
      files: formatsFiles,
      inputId: "formats-upload",
    },
  ]

  const activeConfig = sections.find((s) => s.key === activeSection)
  const auth = getAuth()
  const db = getDatabase()

  // ── Notification logic (unchanged) ──
  const createUploadNotification = async (type) => { /* ... same as before ... */ }
const handleFileChangeWithNotification = async (e) => {
  if (!e.target.files || !e.target.files[0]) return

  try {
    await handleFileUpload(e, activeSection)
    await createUploadNotification(activeSection)
  } catch (err) {
    console.error("Upload failed:", err)
  }
}

  // ── Selection & Delete handlers ──
  const enterSelectMode = () => {
    setSelectMode(true)
    setSelectedFiles(new Set())
  }

  const exitSelectMode = () => {
    setSelectMode(false)
    setSelectedFiles(new Set())
  }

  const toggleFileSelection = (fileId) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(fileId)) newSet.delete(fileId)
      else newSet.add(fileId)
      return newSet
    })
  }

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return
    if (!window.confirm(`Delete ${selectedFiles.size} selected file(s)?`)) return

    for (const fileId of selectedFiles) {
      if (onDeleteFile) await onDeleteFile(activeSection, fileId)
    }

    exitSelectMode()
  }

  const handleDeleteAll = async () => {
    if (!activeConfig?.files?.length) return
    if (!window.confirm(`Delete ALL ${activeConfig.files.length} files? This cannot be undone.`)) return

    if (onDeleteAll) await onDeleteAll(activeSection)
    exitSelectMode()
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date"
    const date = new Date(timestamp)
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const hasFiles = activeConfig?.files?.length > 0

  return (
    <div className="uploads-wrapper">
      {/* LEFT SIDEBAR – unchanged */}
      <div className="uploads-sidebar">
        <h3 className="sidebar-title">Document Types</h3>
        {sections.map((section) => (
          <div
            key={section.key}
            className={`sidebar-item ${activeSection === section.key ? "active" : ""}`}
            onClick={() => {
              setActiveSection(section.key)
              exitSelectMode()
            }}
          >
            {section.label}
          </div>
        ))}
      </div>

      {/* RIGHT CONTENT */}
      <div className="uploads-content">
        {/* Header + Controls */}
        <div className="section-header" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FaFileUpload className="section-icon" />
            <div>
              <h2>{activeConfig?.title}</h2>
              <p>{activeConfig?.description}</p>
            </div>
          </div>

          {hasFiles && (
            <div className="delete-controls">
              {!selectMode ? (
                <>
                  <button className="select-mode-btn" onClick={enterSelectMode}>
                    <FaTrash /> Select to Delete
                  </button>
                  <button className="delete-all-btn" onClick={handleDeleteAll}>
                    <FaTrashAlt /> Delete All
                  </button>
                </>
              ) : (
                <>
                  <button className="cancel-btn" onClick={exitSelectMode}>
                    <FaTimes /> Cancel
                  </button>
                  {selectedFiles.size > 0 && (
                    <button className="delete-selected-btn" onClick={handleDeleteSelected}>
                      <FaTrash /> Delete {selectedFiles.size}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Upload Box – unchanged */}
        <div className="upload-container">
          <input
            type="file"
            id={activeConfig?.inputId}
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChangeWithNotification}
            className="file-input"
          />
          <label htmlFor={activeConfig?.inputId} className="upload-button">
            <FaFileUpload /> Select File
          </label>

          {uploadProgress[activeConfig?.key] != null && (
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{ width: `${uploadProgress[activeConfig?.key]}%` }}
              />
              <span className="progress-text">{uploadProgress[activeConfig?.key]}%</span>
            </div>
          )}
        </div>

        {/* Files List */}
        <div className="files-list">
          <h4>
            <FaFileAlt /> Uploaded Files ({activeConfig?.files?.length || 0})
          </h4>

          {hasFiles ? (
            <ul>
              {activeConfig.files.map((file, index) => {
                const fileId = file.id || file.name || String(index)
                const isSelected = selectedFiles.has(fileId)
                const uploadDate = file.uploadDate || file.createdAt || file.timestamp

                return (
                  <li
                    key={fileId}
                    className={`file-item ${selectMode ? "selectable" : ""} ${isSelected ? "selected" : ""}`}
                  >
                    {selectMode && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleFileSelection(fileId)}
                        className="file-checkbox"
                      />
                    )}

                    <FaFileAlt className="file-icon" />

                    <div className="file-info">
                      <span className="file-name" title={file.name}>
                        {file.name}
                      </span>
                      <span className="file-date">{formatDate(uploadDate)}</span>
                    </div>

                    <div className="file-actions">
                      <button
                        onClick={() => window.open(file.url, "_blank")}
                        className="action-btn view-btn"
                        title="View File"
                      >
                        <FaEye />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="no-files">No files uploaded yet</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default UploadsSection